"use client";

import * as React from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { PusherEvent, RewardReceivedPayload, TodoTaskCompletedPayload, UserLangProgressPayload } from "@/lib/pusher/pusher-tools";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "@heroui/modal";
import { useUserLocale } from "@/hooks/useUserLocale";
import { Button } from "@heroui/button";
import { XpGainedDetailedBadge } from "@/components/ui/xp/XpGainedDetailedBadge";
import { EventQueueItem } from "@/providers/ServiceModalsProviderEventQueueItem";
import { loadAchievementRewards, loadCurrentUserLangProgress } from "@/store/actions/app";
import { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import { DEFAULT_TODO_LIST_TAG, XP_POINTS } from "@/lib/constants";
import { TodoItem } from "../Types";
import { Image } from "@heroui/image";
import { useSfxProvider } from "@/hooks/useSfxProvider";

export default function RewardReceivedModal({ isOpen, onClose, event }: { isOpen: boolean, onClose: () => void, event: EventQueueItem }) {
    const { t, locale } = useUserLocale();

    const dispatch = useDispatch();

    const languageProgress = useSelector((state: RootState) => state.app.languageProgress);
    const todoLists = useSelector((state: RootState) => state.app.todoLists);
    const eventPayload = event.payload as RewardReceivedPayload;    
    const [todoItem, setTodoItem] = useState<TodoItem | null>(null);
    const [shimmer, setShimmer] = useState(false);

    const loadedLanguageProgressOnce = useRef(false);

    const sfxProvider = useSfxProvider();

    useEffect(() => {
        if (languageProgress && eventPayload && !loadedLanguageProgressOnce.current) {
            loadedLanguageProgressOnce.current = true;
            loadAchievementRewards(dispatch, languageProgress.language); // Refresh store rewards data
        }
    }, [eventPayload, languageProgress, loadedLanguageProgressOnce]);    

    useEffect(() => {
        const triggerMetallicShimmer = () => {
          // Random delays between 5-20 seconds
          const delay = 2500 + Math.random() * 5500;
          
          setTimeout(() => {
            if (Math.random() > 0.3) { // 70% chance to trigger
              setShimmer(true);
              
              // Reset metallic shimmer after animation completes
              setTimeout(() => {
                setShimmer(false);
                triggerMetallicShimmer(); // Schedule next shimmer
              }, 1500); // Animation duration
            } else {
              triggerMetallicShimmer(); // Try again if not triggered
            }
          }, delay);
        };
        
        setShimmer(true);
        triggerMetallicShimmer();
        
        return () => {
          // Cleanup handled by the timeouts themselves
        };
      }, []);

    // Take the todo item from the payload, or if only _id is provided, get it from the todo lists in the store
    useEffect(() => {
        if (eventPayload?.todoItemTag && todoLists) {
            const todoItem = todoLists[DEFAULT_TODO_LIST_TAG]?.items.find(item => item.tag === eventPayload.todoItemTag);            
            setTodoItem(todoItem);
        }
    }, [eventPayload, todoLists]);

    useEffect(() => {
        sfxProvider.playSfx("/sfx/great-job-applause.mp3");
    }, []);

    return (
        <Modal isOpen={isOpen} placement="center" onOpenChange={onClose} classNames={{backdrop: 'bg-white/75 backdrop-blur-sm'}}>
            <ModalContent>
                <ModalBody className="overflow-x-hidden">
                    <div className="flex flex-row items-center gap-2 text-center justify-center mt-2 font-bold text-xl text-gray-600">
                        {t.todoLists.modals.rewardReceived.title}                    
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2 items-center overflow-x-hidden">
                            <Image src={eventPayload ? process.env.NEXT_PUBLIC_DYNAMIC_ASSETS_BASE_URL + eventPayload.rewardItem.imageUrl + "_sm.jpg" : null} alt={eventPayload.rewardItem.name ?? ""} width={256} height={256} />
                            {shimmer && <div className="reward-metallic-sweep-overlay absolute mt-12 w-64 h-64 inset-0 pointer-events-none" />}

                            <div className="text-lg font-semibold">{eventPayload?.rewardItem?.name}</div>
                        </div>

                        <div className="text-sm text-gray-500">
                            {eventPayload?.rewardItem?.description}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className='flex items-center gap-2 justify-between w-full'>
                        <div></div>
                        <Button onPress={() => onClose()} color='success' size='lg' variant='shadow'>{t.common.continue}</Button>
                    </div>
                </ModalFooter>
            </ModalContent>

            <style jsx global>{`
                @keyframes reward-metallic-sweep {
                    0% {
                        transform: translateX(-48%);
                        opacity: 0;
                    }
                    10% {
                        opacity: 1;
                    }
                    60% {
                        opacity: 0.8;
                    }
                    100% {
                        transform: translateX(48%);
                        opacity: 0;
                    }
                }
                
                .reward-metallic-sweep-overlay {
                    background: linear-gradient(90deg, 
                        transparent 0%, 
                        rgba(255, 255, 255, 0) 10%, 
                        rgba(255, 255, 255, 0.8) 50%, 
                        rgba(255, 255, 255, 0) 90%, 
                        transparent 100%);
                    transform: skewX(-20deg);
                    animation: reward-metallic-sweep 1.4s ease-in-out forwards;
                    mix-blend-mode: overlay;
                    z-index: 10;
                }

                .dark .reward-metallic-shimmer-overlay {
                    display: none;
                }
            `}</style>
        </Modal>        
    );
}
